import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search, BookOpen, Volume2, Globe, Languages, Loader2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const DictionaryPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState('fr');
  const [history, setHistory] = useState([]);

  const handleSearch = useCallback(async (searchTerm) => {
    const word = (searchTerm || query).trim();
    if (!word) return;

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const res = await axios.get(`${API}/api/dictionary/search`, {
        params: { word, lang }
      });
      const data = res.data;
      if (data.results && data.results.length > 0) {
        setResults(data.results);
        setHistory(prev => {
          const filtered = prev.filter(h => h.word !== word || h.lang !== lang);
          return [{ word, lang, timestamp: Date.now() }, ...filtered].slice(0, 20);
        });
      } else {
        setError(`Mot "${word}" non trouvé dans le dictionnaire ${lang === 'fr' ? 'français' : 'anglais'}.`);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError(`Mot "${word}" non trouvé dans le dictionnaire ${lang === 'fr' ? 'français' : 'anglais'}.`);
      } else {
        setError('Erreur de connexion. Vérifiez votre connexion internet.');
      }
    } finally {
      setLoading(false);
    }
  }, [query, lang]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const playAudio = (url) => {
    if (url) {
      const audio = new Audio(url);
      audio.play().catch(() => {});
    }
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="dictionary-page">
      <div>
        <h1 className="heading-2">Dictionnaire</h1>
        <p className="text-gray-500 mt-1">Recherchez la définition de n'importe quel mot</p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Tabs value={lang} onValueChange={setLang} className="flex-shrink-0">
              <TabsList>
                <TabsTrigger value="fr" data-testid="lang-fr">
                  <Globe className="w-3.5 h-3.5 mr-1" /> FR
                </TabsTrigger>
                <TabsTrigger value="en" data-testid="lang-en">
                  <Languages className="w-3.5 h-3.5 mr-1" /> EN
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex-1 relative">
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={lang === 'fr' ? 'Chercher un mot en français...' : 'Search a word in English...'}
                className="pr-10"
                data-testid="dictionary-search-input"
              />
              <Search className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
            </div>
            <Button onClick={() => handleSearch()} disabled={loading} className="gap-2" data-testid="dictionary-search-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Chercher
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-orange-200 bg-orange-50/30" data-testid="dictionary-error">
          <CardContent className="p-6 text-center">
            <BookOpen className="w-12 h-12 text-orange-300 mx-auto mb-3" />
            <p className="text-orange-700">{error}</p>
            <p className="text-xs text-orange-500 mt-2">Essayez un autre mot ou changez de langue.</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && results.map((entry, idx) => (
        <Card key={idx} data-testid={`dict-result-${idx}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-900">{entry.word}</h2>
              {entry.source && (
                <Badge variant="outline" className="text-xs text-gray-400">
                  {entry.source === 'fr.wiktionary.org' ? 'Wiktionnaire' : 'Dictionary'}
                </Badge>
              )}
              {entry.phonetics?.map((p, pi) => p.audio ? (
                <Button key={pi} variant="ghost" size="icon" className="w-8 h-8" onClick={() => playAudio(p.audio)} data-testid={`play-audio-${pi}`}>
                  <Volume2 className="w-4 h-4 text-blue-600" />
                </Button>
              ) : null)}
            </div>
            {entry.phonetic && <p className="text-sm text-gray-500 font-mono">{entry.phonetic}</p>}
            {entry.origin && <p className="text-xs text-gray-400 mt-1">Origine: {entry.origin}</p>}
          </CardHeader>
          <CardContent className="space-y-4">
            {entry.meanings?.map((meaning, mi) => (
              <div key={mi}>
                <Badge className="bg-blue-50 text-blue-700 border-blue-200 mb-2">
                  {meaning.partOfSpeech}
                </Badge>
                <ol className="space-y-2 ml-1">
                  {meaning.definitions?.map((def, di) => (
                    <li key={di} className="flex gap-2">
                      <span className="text-gray-400 text-sm flex-shrink-0">{di + 1}.</span>
                      <div>
                        <p className="text-sm text-gray-800">{def.definition}</p>
                        {def.example && (
                          <p className="text-xs text-gray-500 mt-0.5 italic">« {def.example} »</p>
                        )}
                        {def.synonyms?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {def.synonyms.slice(0, 5).map((s, si) => (
                              <span key={si} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                                onClick={() => { setQuery(s); handleSearch(s); }}>
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
                {meaning.synonyms?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-400 mb-1">Synonymes:</p>
                    <div className="flex flex-wrap gap-1">
                      {meaning.synonyms.slice(0, 8).map((s, si) => (
                        <span key={si} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full cursor-pointer hover:bg-green-100"
                          onClick={() => { setQuery(s); handleSearch(s); }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {meaning.antonyms?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-400 mb-1">Antonymes:</p>
                    <div className="flex flex-wrap gap-1">
                      {meaning.antonyms.slice(0, 8).map((a, ai) => (
                        <span key={ai} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full cursor-pointer hover:bg-red-100"
                          onClick={() => { setQuery(a); handleSearch(a); }}>
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {/* No search yet */}
      {!results && !error && !loading && (
        <Card>
          <CardContent className="p-10 text-center">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {lang === 'fr' ? 'Dictionnaire Français' : 'English Dictionary'}
            </h3>
            <p className="text-gray-500 text-sm">
              {lang === 'fr'
                ? 'Tapez un mot et appuyez sur Entrée pour voir sa définition. Source : Wiktionnaire français.'
                : 'Type a word and press Enter to see its definition.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm text-gray-500">Historique</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {history.map((h, i) => (
                <span key={i} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full cursor-pointer hover:bg-blue-100 hover:text-blue-700 transition-colors"
                  onClick={() => { setQuery(h.word); setLang(h.lang); handleSearch(h.word); }}
                  data-testid={`history-${i}`}>
                  {h.word} <span className="text-xs text-gray-400">({h.lang})</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DictionaryPage;
